import reactRenderer from "~/lib/reactRenderer";
import merge from "lodash/merge";
import Chart from "~/components/shared/Chart";
import UsageTooltip from "~/components/shared/UsageChart/UsageTooltip";

// Reuse the React root so Chart.js hover updates don't remount the popover.
const tooltipRoots = new WeakMap<HTMLDivElement, ReturnType<typeof reactRenderer>>();

const findOrCreateTooltip = (canvas: HTMLCanvasElement) => {
  const parent = canvas.parentElement;
  if (parent == null) {
    return null;
  }

  let tooltipEl = parent.querySelector<HTMLDivElement>("#chartjs-tooltip");
  if (!tooltipEl) {
    tooltipEl = document.createElement("div");
    tooltipEl.id = "chartjs-tooltip";
    tooltipEl.className =
      "absolute bottom-0 left-0 z-10 pointer-events-none transform duration-100 shadow-xs translate-y-full";
    parent.appendChild(tooltipEl);
  }

  return tooltipEl;
};

const renderTooltip = (context: any) => {
  const { tooltip, chart } = context;

  const tooltipEl = findOrCreateTooltip(chart.canvas);
  if (tooltipEl == null) {
    return;
  }

  if (tooltip.opacity === 0) {
    tooltipEl.style.opacity = "0";
    return;
  }

  tooltipEl.style.opacity = "1";
  tooltipEl.style.transform = `translate(${tooltip.caretX}px,0)`;

  const root = tooltipRoots.get(tooltipEl);
  const nextRoot = reactRenderer(<UsageTooltip tooltip={tooltip} />, tooltipEl, root);
  tooltipRoots.set(tooltipEl, nextRoot);
};

// Builds the tooltip label for a point. Some charts (e.g. byte sizes) give us a
// nicely formatted string to show; if there isn't one, we just show the raw value.
const tooltipLabel = (context: any) => {
  const value =
    context.raw?.formatted ??
    context.dataset?.formattedData?.[context.dataIndex] ??
    context.formattedValue;
  return `${context.dataset.label}: ${value}`;
};

type DateAnchorTickContext = {
  options: {
    time: {
      unit?: string;
    };
  };
};

type DateAnchorScale = DateAnchorTickContext & {
  min: number;
  max: number;
  ticks: { value: number }[];
};

const dateAnchorColor = "#BBBBBB";

const hasBarDataset = (chartData: any) =>
  chartData.type === "bar" ||
  chartData.data.datasets.some((dataset: any) => dataset.type === "bar");

const formatDateAnchor = (date: Date, unit?: string) => {
  const month = date.toLocaleDateString("en-US", { month: "short" });
  const year = `'${date.toLocaleDateString("en-US", { year: "2-digit" })}`;

  return unit === "month" ? `${month} ${year}` : `${date.getDate()} ${month} ${year}`;
};

const parseChartDate = (dateStr: string) => {
  const date = new Date(dateStr);
  return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
};

function dateAnchorTickLabel(
  this: DateAnchorTickContext,
  tickValue: number,
  index: number,
  ticks: unknown[],
) {
  if (index !== 0 && index !== ticks.length - 1) {
    return "";
  }

  return formatDateAnchor(new Date(tickValue), this.options.time.unit);
}

const buildDateAnchorTicks = (axis: DateAnchorScale) => {
  const max = new Date(axis.max);
  const values =
    axis.options.time.unit === "month"
      ? [axis.min, new Date(max.getFullYear(), max.getMonth(), 1).getTime()]
      : [axis.min, axis.max];

  axis.ticks = Array.from(new Set(values)).map((value) => ({ value }));
};

const getUsageChartOptions = (chartData: any) => {
  const options = {
    layout: {
      padding: 4,
    },
    maintainAspectRatio: false,
    clip: 8,
    interaction: {
      mode: "index",
      intersect: false,
    },
    elements: {
      point: {
        hoverRadius: 4,
        hoverBorderWidth: 2,
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: false,
        mode: "index",
        yAlign: "bottom",
        external: renderTooltip,
        callbacks: {
          label: tooltipLabel,
        },
      },
    },
    scales: {
      // eslint-disable-next-line id-length
      x: {
        border: {
          color: dateAnchorColor,
          // eslint-disable-next-line id-length
          z: 1,
        },
        grid: {
          display: false,
        },
        time: {
          parser: parseChartDate,
        },
        ticks: {
          autoSkip: false,
          align: "center",
          callback: dateAnchorTickLabel,
          maxRotation: 0,
          font: {
            weight: 500,
            size: 11,
          },
        },
        afterBuildTicks: buildDateAnchorTicks,
      },
      // eslint-disable-next-line id-length
      y: {
        display: false,
        beginAtZero: true,
      },
      y1: {
        display: false,
        beginAtZero: true,
      },
    },
  } as const;

  const chartOptions = {
    ...chartData,
    options: merge({}, options, chartData.options),
  };

  const xScale = chartOptions.options.scales.x;
  xScale.ticks.color = xScale.border.color;
  xScale.offset = xScale.time.unit === "month" && hasBarDataset(chartOptions);

  return chartOptions;
};

type UsageChartProps = {
  chartData: any;
  height: number;
  canvasId?: string;
};

const UsageChart = ({ chartData, ...rest }: UsageChartProps) => {
  const chartOptions = getUsageChartOptions(chartData);
  return <Chart chartOptions={chartOptions} {...rest} />;
};

export default UsageChart;
